import { useInfiniteQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { getEntityAssets } from 'apis/asset-repository-service/api';
import { Asset, AssetScope, Image } from 'apis/asset-repository-service/types';

import { useScopeContext } from '../contexts/ScopeContext';

type Props = {
  size?: number;
};

const isAssetImage = (asset: Asset): asset is Image => asset.contentType.startsWith('image/'); // filter out non-image assets

const isAssetScope = (scope: string): scope is AssetScope['type'] =>
  ['venue', 'merchant', 'brand', 'country', 'unspecified'].includes(scope);

export const useListingManagerImageGallery = ({ size = 12 }: Props = {}) => {
  const scope = useScopeContext();
  const queryKey = useMemo(() => ['listing-manager-images-gallery', size, scope.type, scope.id], [size, scope]);

  const { data, ...rest } = useInfiniteQuery({
    queryKey,
    queryFn: ({ pageParam: cursor }) =>
      getEntityAssets({
        scope: isAssetScope(scope.type) ? scope.type : 'unspecified',
        scopeId: scope.id,
        source: 'self_service',
        size,
        cursor,
      }).then(response => ({
        paged: response.paged,
        content: response.content?.filter(isAssetImage) ?? [],
      })),
    getNextPageParam: lastPage => lastPage.content[lastPage.content?.length - 1]?.id,
    refetchOnWindowFocus: false,
    retry: false,
  });

  const imageAssets = data?.pages.flatMap(page => page.content) ?? [];

  return {
    ...rest,
    isFeatureEnabled: imageAssets.length > 0,
    imageAssets,
  };
};
